import { KeyboardEvent, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import * as XLSX from 'xlsx';
import {
  fetchEnderecosUtilizados,
  fetchEtiquetasGrupo,
  fetchExportEstoqueData,
  fetchRelatorioEstoque,
  fetchRelatorioVarejo,
  relatorioEstoqueKeys,
} from '@/services/relatorioEstoqueService';
import type {
  IEstoqueGrupo,
  IExportEstoqueItem,
  IRelatorioEstoqueSearch,
  TFilterField,
  TDashboardFilter,
} from '@/types/relatorioEstoque';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import { useAnalytics, useErrors, useLogger } from '@/observability';
import './relatorio-estoque.css';

export const Route = createFileRoute('/_authenticated/estoque/relatorio-estoque/')({
  component: RelatorioEstoquePage,
});

const INITIAL_SEARCH: IRelatorioEstoqueSearch = {
  filterField: 'codigo_produto',
  filterValue: '',
};

const DIAS_VALIDADE_PROXIMA = 30;

function excelSerialParaData(excelDate: number | string): Date {
  const dataBase = new Date(1899, 11, 30);
  dataBase.setDate(dataBase.getDate() + parseInt(String(excelDate), 10));
  return dataBase;
}

function formatarData(excelDate: number | string | undefined): string {
  if (!excelDate) return '';
  return excelSerialParaData(excelDate).toLocaleDateString('pt-BR');
}

type TValidadeStatus = 'vencida' | 'proxima' | '';

function getValidadeStatus(excelDate: number | string | undefined): TValidadeStatus {
  if (!excelDate) return '';
  const dataValidade = excelSerialParaData(excelDate);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const diffDias = Math.ceil((dataValidade.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDias < 0) return 'vencida';
  if (diffDias <= DIAS_VALIDADE_PROXIMA) return 'proxima';
  return '';
}

const columns: string[] = ['Código Produto', 'Descrição', 'Lote', 'Validade', 'Endereço', 'Peso Líquido (KG)', 'Caixas'];

function RelatorioEstoquePage() {
  const analytics = useAnalytics();
  const errors = useErrors();
  const logger = useLogger();
  const [filterField, setFilterField] = useState<TFilterField>('codigo_produto');
  const [filterValue, setFilterValue] = useState('');
  const [activeSearch, setActiveSearch] = useState<IRelatorioEstoqueSearch>(INITIAL_SEARCH);
  const [selectedGroup, setSelectedGroup] = useState<IEstoqueGrupo | null>(null);
  const [actionMessage, setActionMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEnderecoModal, setShowEnderecoModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const {
    data: estoqueData,
    isError: isEstoqueError,
    error: estoqueError,
  } = useQuery({
    queryKey: relatorioEstoqueKeys.estoque(activeSearch),
    queryFn: () => fetchRelatorioEstoque(activeSearch),
  });

  const {
    data: etiquetasData,
    isError: isEtiquetasError,
    error: etiquetasError,
  } = useQuery({
    queryKey: relatorioEstoqueKeys.etiquetas(selectedGroup),
    queryFn: () => fetchEtiquetasGrupo(selectedGroup!),
    enabled: selectedGroup !== null,
  });

  const { data: enderecosData } = useQuery({
    queryKey: relatorioEstoqueKeys.enderecos,
    queryFn: fetchEnderecosUtilizados,
    enabled: showEnderecoModal,
  });

  const estoque = estoqueData?.estoque ?? [];
  const totalCodigosUnicos = estoqueData?.total_codigos_unicos ?? 0;
  const totalEnderecos = estoqueData?.total_enderecos ?? 0;
  const totalPeso = estoqueData?.peso_total_geral ?? 0;
  const caixasValidadeProxima = estoqueData?.caixas_validade_proxima ?? [];
  const totalCaixas = estoqueData?.total_caixas ?? 0;
  const pesoValidadeProxima = caixasValidadeProxima.reduce((acc, item) => acc + item[5], 0);
  const etiquetas = etiquetasData?.etiquetas ?? [];
  const enderecos = enderecosData?.enderecos ?? [];
  const totalKgEnderecos = enderecos.reduce((acc, endereco) => acc + Number(endereco.KG), 0);

  const handleFilter = () => {
    setActionMessage('');
    setSelectedGroup(null);
    setActiveSearch({
      filterField,
      filterValue,
    });
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setActionMessage('');
    setSelectedGroup(null);
    setActiveSearch(INITIAL_SEARCH);
  };

  const handleKpiKeyDown = (event: KeyboardEvent<HTMLDivElement>, filterType: TDashboardFilter) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDashboardClick(filterType);
    }
  };

  const handleDashboardClick = (filterType: TDashboardFilter) => {
    if (filterType === 'validade_proxima') {
      setShowModal(true);
    } else if (filterType === 'enderecos_utilizados') {
      setShowEnderecoModal(true);
    } else {
      setActionMessage('');
      setSelectedGroup(null);
      setActiveSearch({
        filterField,
        filterValue: '',
        dashboardFilter: filterType,
      });
    }
  };

  const handleRowClick = (grupo: IEstoqueGrupo) => {
    setActionMessage('');
    setSelectedGroup(grupo);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportError('');

    try {
      const result = await fetchExportEstoqueData();
      if (!Array.isArray(result)) {
        throw new Error('Os dados recebidos não estão no formato esperado.');
      }

      const reportData: IExportEstoqueItem[] = result.map((item) => ({
        ...item,
        peso_liquido: parseFloat(Number(item.peso_liquido).toFixed(2)),
        caixas: parseInt(String(item.caixas), 10),
      }));

      const worksheet = XLSX.utils.json_to_sheet(reportData);

      Object.keys(worksheet).forEach((cell) => {
        if (worksheet[cell].v && !isNaN(worksheet[cell].v as number) && worksheet[cell].t === 'n') {
          worksheet[cell].z = '#,##0.00';
        }
      });

      worksheet['!cols'] = [{ wch: 13 }, { wch: 95 }, { wch: 15 }, { wch: 10 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Estoque Report');
      XLSX.writeFile(workbook, 'Estoque_report.xlsx');
      analytics.track('Estoque Report Exported');
    } catch (err) {
      errors.capture(err, { tags: { source: 'relatorio-estoque.export' } });
      logger.error('Estoque report export failed');
      const msg = err instanceof Error ? err.message : 'Erro ao buscar dados do relatório.';
      setExportError(msg);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportRelatorioVarejo = async () => {
    setIsExporting(true);
    setExportError('');

    try {
      const result = await fetchRelatorioVarejo('500G');

      if (!Array.isArray(result)) {
        throw new Error('Os dados recebidos não estão no formato esperado.');
      }

      const reportData = result.map((item) => ({
        codigo_produto: item.codigo_produto || 'N/A',
        descricao: item.descricao || 'N/A',
        peso_liquido: parseFloat(Number(item.peso_liquido || 0).toFixed(2)),
        caixas: parseInt(String(item.caixas || 0), 10),
        validade: formatarData(item.validade),
      }));

      const worksheet = XLSX.utils.json_to_sheet(reportData);
      worksheet['!cols'] = [{ wch: 13 }, { wch: 50 }, { wch: 15 }, { wch: 10 }, { wch: 15 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatorio Varejo');
      XLSX.writeFile(workbook, 'Relatorio_Varejo.xlsx');
      analytics.track('Varejo Report Exported');
    } catch (err) {
      errors.capture(err, { tags: { source: 'relatorio-estoque.varejo-export' } });
      logger.error('Varejo report export failed');
      const msg = err instanceof Error ? err.message : 'Erro ao buscar dados do relatório.';
      setExportError(msg);
    } finally {
      setIsExporting(false);
    }
  };

  const queryErrorMessage = isEstoqueError
    ? estoqueError instanceof Error
      ? estoqueError.message
      : estoqueData?.error || 'Erro ao carregar o estoque'
    : '';

  const etiquetasErrorMessage =
    isEtiquetasError && selectedGroup
      ? etiquetasError instanceof Error
        ? etiquetasError.message
        : 'Erro ao buscar etiquetas'
      : '';

  const etiquetasInfoMessage =
    selectedGroup && !isEtiquetasError && etiquetas.length === 0
      ? 'Nenhuma etiqueta encontrada para os parâmetros fornecidos.'
      : '';

  const message = actionMessage || queryErrorMessage || etiquetasErrorMessage || etiquetasInfoMessage;

  return (
    <div className="estoque-geral-container">
      <div className="estoque-geral-header">
        <Card
          onClick={() => handleDashboardClick('codigos_unicos')}
          onKeyDown={(e) => handleKpiKeyDown(e, 'codigos_unicos')}
          role="button"
          tabIndex={0}
          aria-label="Filtrar estoque por códigos únicos"
        >
          <Card.Header>
            <h3>Códigos Únicos</h3>
          </Card.Header>
          <Card.Content>
            <p>{totalCodigosUnicos}</p>
          </Card.Content>
        </Card>

        <Card
          onClick={() => handleDashboardClick('enderecos_utilizados')}
          onKeyDown={(e) => handleKpiKeyDown(e, 'enderecos_utilizados')}
          role="button"
          tabIndex={0}
          aria-label="Abrir detalhamento de endereços utilizados"
        >
          <Card.Header>
            <h3>Endereços Utilizados</h3>
          </Card.Header>
          <Card.Content>
            <p>{totalEnderecos}</p>
          </Card.Content>
        </Card>

        <Card
          onClick={() => handleDashboardClick('validade_proxima')}
          onKeyDown={(e) => handleKpiKeyDown(e, 'validade_proxima')}
          role="button"
          tabIndex={0}
          aria-label="Abrir detalhamento de produtos próximos do vencimento"
        >
          <Card.Header>
            <h3>Próximos Vencimento</h3>
          </Card.Header>
          <Card.Content>
            <p>
              {caixasValidadeProxima.reduce((acc, row) => acc + (row[4] ?? 0), 0)} caixas /{' '}
              {Number(pesoValidadeProxima.toFixed(2)).toLocaleString('pt-BR')} Kg
            </p>
          </Card.Content>
        </Card>

        <Card
          onClick={() => handleDashboardClick('total_caixas')}
          onKeyDown={(e) => handleKpiKeyDown(e, 'total_caixas')}
          role="button"
          tabIndex={0}
          aria-label="Filtrar estoque por total de caixas"
        >
          <Card.Header>
            <h3>Total de Caixas</h3>
          </Card.Header>
          <Card.Content>
            <p>{Number(totalCaixas).toLocaleString('pt-BR')}</p>
          </Card.Content>
        </Card>

        <Card
          onClick={() => handleDashboardClick('peso_total')}
          onKeyDown={(e) => handleKpiKeyDown(e, 'peso_total')}
          role="button"
          tabIndex={0}
          aria-label="Filtrar estoque por peso total"
        >
          <Card.Header>
            <h3>Peso Total (Kg)</h3>
          </Card.Header>
          <Card.Content>
            <p>{Number(totalPeso.toFixed(2)).toLocaleString('pt-BR')} Kg</p>
          </Card.Content>
        </Card>
      </div>

      <p className="estoque-geral-header-hint">
        Clique em um indicador para filtrar ou detalhar os dados.
      </p>

      <div className="estoque-geral-content">
        <Card className="filter">
          <Card.Content className="filter-card__content">
            <div className="filter-grid">
              <div className="input-content filter-grid__field">
                <label htmlFor="filterField">Campo de Filtro</label>
                <select
                  id="filterField"
                  value={filterField}
                  onChange={(e) => setFilterField(e.target.value as TFilterField)}
                >
                  <option value="codigo_produto">Código Produto</option>
                  <option value="lote">Lote</option>
                  <option value="descricao">Descrição</option>
                  <option value="endereco">Endereço</option>
                </select>
              </div>

              <div className="input-content filter-grid__value">
                <label htmlFor="filterValue">Valor para busca</label>
                <input
                  id="filterValue"
                  type="text"
                  placeholder="Digite o termo para pesquisar..."
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                />
              </div>

              <button
                type="button"
                onClick={handleFilter}
                className="filter-button"
              >
                Filtrar
              </button>

              <button
                type="button"
                onClick={handleClearFilter}
                className="clear-filter-button"
              >
                Limpar Filtro
              </button>
            </div>

            <div
              className="filter-card__divider"
              aria-hidden="true"
            />

            <div className="filter-card__exports">
              <button
                type="button"
                onClick={() => void handleExport()}
                disabled={isExporting}
                className="export-button export-button--estoque"
              >
                {isExporting ? 'Exportando...' : 'Exportar Relatório de Estoque'}
              </button>

              <button
                type="button"
                onClick={() => void handleExportRelatorioVarejo()}
                disabled={isExporting}
                className="export-button export-button--varejo"
              >
                {isExporting ? 'Exportando...' : 'Exportar Relatório Varejo'}
              </button>

              {exportError && <p className="filter-card__export-error">{exportError}</p>}
            </div>
          </Card.Content>
        </Card>

        <div className="estoque-table-container">
          <Table className="estoque-table">
            <caption className="sr-only">Estoque geral agrupado</caption>
            <Table.Header>
              <Table.Row>
                {columns.map((item, index) => (
                  <Table.Head
                    key={index}
                    scope="col"
                  >
                    {item}
                  </Table.Head>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {estoque.map((item, index) => {
                const validadeStatus = getValidadeStatus(item.validade);
                return (
                  <Table.Row
                    key={index}
                    onClick={() => handleRowClick(item)}
                    className={[
                      selectedGroup === item ? 'selected-row' : '',
                      validadeStatus ? `row-validade--${validadeStatus}` : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <Table.Cell>{item.codigo_produto}</Table.Cell>
                    <Table.Cell>{item.descricao}</Table.Cell>
                    <Table.Cell>{item.lote}</Table.Cell>
                    <Table.Cell
                      className={validadeStatus ? `validade--${validadeStatus}` : ''}
                    >
                      {formatarData(item.validade)}
                    </Table.Cell>
                    <Table.Cell>{item.endereco}</Table.Cell>
                    <Table.Cell>
                      {item.peso_total ? item.peso_total.toFixed(2) : '0.00'} Kg
                    </Table.Cell>
                    <Table.Cell>{item.caixas}</Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Content>
          </Table>
        </div>
      </div>

      {showModal && (
        <Modal
          className="relatorio-estoque-modal"
          onClose={() => setShowModal(false)}
        >
          <Modal.Header title="Produtos Próximos do Vencimento" />
          <Modal.Content>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Código Produto</Table.Head>
                  <Table.Head>Descrição</Table.Head>
                  <Table.Head>Endereço</Table.Head>
                  <Table.Head>Validade</Table.Head>
                  <Table.Head>Quantidade de Caixas</Table.Head>
                  <Table.Head>Peso Total (Kg)</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Content>
                {caixasValidadeProxima.map((item, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{item[0]}</Table.Cell>
                    <Table.Cell>{item[1]}</Table.Cell>
                    <Table.Cell>{item[2] || '-'}</Table.Cell>
                    <Table.Cell>{formatarData(item[3])}</Table.Cell>
                    <Table.Cell>{item[4]}</Table.Cell>
                    <Table.Cell>{item[5].toFixed(2)} Kg</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Content>
            </Table>
          </Modal.Content>
        </Modal>
      )}

      {showEnderecoModal && (
        <Modal
          className="relatorio-estoque-modal"
          onClose={() => setShowEnderecoModal(false)}
        >
          <Modal.Header title="Endereços Utilizados" />
          <Modal.Content>
            <h4 className="modalestoque-header">
              Total KG: {Number(totalKgEnderecos).toLocaleString('pt-BR')}
            </h4>
            <div className="enderecos-container">
              {enderecos.map((endereco, index) => (
                <Card
                  key={index}
                  className="endereco-bloco"
                >
                  <Card.Header>
                    <h3>{endereco.endereco}</h3>
                  </Card.Header>
                  <Card.Content>
                    <p>
                      <strong>Caixas:</strong> {endereco.caixas}
                    </p>
                    <p>
                      <strong>Peso:</strong> {Number(endereco.KG).toLocaleString('pt-BR')} Kg
                    </p>
                  </Card.Content>
                </Card>
              ))}
            </div>
          </Modal.Content>
        </Modal>
      )}

      {etiquetas.length > 0 && (
        <div className="etiquetas-section">
          <h3>Etiquetas do Grupo Selecionado</h3>
          <Table className="etiquetas-table">
            <Table.Header>
              <Table.Row>
                <Table.Head>Código de Barras</Table.Head>
                <Table.Head>Código Produto</Table.Head>
                <Table.Head>Lote</Table.Head>
                <Table.Head>Endereço</Table.Head>
                <Table.Head>Peso Líquido (Kg)</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Content>
              {etiquetas.map((etiqueta, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{etiqueta.codigo_barras}</Table.Cell>
                  <Table.Cell>{etiqueta.codigo_produto}</Table.Cell>
                  <Table.Cell>{etiqueta.lote}</Table.Cell>
                  <Table.Cell>{etiqueta.endereco}</Table.Cell>
                  <Table.Cell>
                    {etiqueta.peso_liquido ? etiqueta.peso_liquido.toFixed(2) : '0.00'} Kg
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Content>
          </Table>
        </div>
      )}
    </div>
  );
}
